import React from 'react';
import Immutable from 'immutable';
import ParentBoundComponent from '../../components/parentboundcomponent';
import RenderContext from '../../components/definitions/rendercontext';
import Binder from '../../components/definitions/binder';
import ModuleContainer from '../../components/containers/modulecontainer';
import Button from '../../components/forms/button';
import EntityDetailModel, { errorPath, errorMessageListPath, readOnlyPath } from './entitydetailmodel';
import { getLookup } from '../../data/lookupstore';
import styled from 'styled-components';
import {defaultTheme} from '../../data/themes';
import { getPhrase } from '../../data/phrases';
import Module from '../module';

const EMPTY_MAP = Immutable.Map({});



export default class EntityDetailModule extends ParentBoundComponent {
	constructor() {
		super();

		this.state = {
			errors: EMPTY_MAP
		};

		this.binder = new Binder({
			getData: this.getData,
			setData: this.setData,
			handleEvent: this.handleEvent,
			getLookup,
			errorPath,
			readOnlyPath,
			getModel: this.getModel,
			setModel: this.setModel
		}, this);

		this.save = this.save.bind(this);
		this.cancelChanges = this.cancelChanges.bind(this);
		this.clearErrorMessages = this.clearErrorMessages.bind(this);
		this.onModalCloseRequested = this.onModalCloseRequested.bind(this);
	}

	onModalCloseRequested(params) {
		this.setModel(this.getModel().deleteModalModule());
		const onModalModuleClosed = this.getData().get('onModalModuleClosed');
		if (onModalModuleClosed) {
			onModalModuleClosed(params);
		}
	}

	getModel() {
		return new EntityDetailModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	async save(ignoreIgnorableErrors) {
		let model = new EntityDetailModel(this.getData());
		model = await model.saveDetail(ignoreIgnorableErrors);
		this.setData(model.getData());
	}

	async cancelChanges() {
		let model = new EntityDetailModel(this.getData());
		model = await model.resetToOriginal();
		this.setData(model.getData());
	}

	handleEvent(eventType, params) {
	}

	getHeader(data, ctx) {
		const header = data.get('header');
		if (typeof header === 'function') {
			return header(data, ctx);
		} else {
			return header;
		}
	}

	hasErrors() {
		return this.getErrors().size > 0;
	}

	getErrors() {
		return this.getData().getIn(errorPath);
	}

	clearErrorMessages() {
		let model = new EntityDetailModel(this.getData());
		model = model.clearErrorMessages();
		this.setData(model.getData());
	}

	async validateAll() {
		let model = new EntityDetailModel(this.getData());
		model = await model.validateAll();
		this.setData(model.getData());
	}

	getLifeCycleListener() {
		return this.getData().get('lifeCycleListener');
	}

	createRenderContext() {
		const data = this.getData();
		return new RenderContext(this.binder).withCalculatedProperties(data.get('calculateContextProperties'));
	}

	componentDidMount() {
		console.log('mounting');
		const listener = this.getLifeCycleListener();
		if (listener) {
			listener({
				type: 'mount',
				ctx: this.createRenderContext()
			});
		}
	}

	componentWillUnmount() {
		const listener = this.getLifeCycleListener();
		if (listener) {
			listener({
				type: 'unmount',
				ctx: this.createRenderContext()
			});
		}
	}

	isReadOnly(data, ctx) {
		const readOnly = data.get('readOnly');
		if (!readOnly) {
			return false;
		}
		if (typeof readOnly === 'function') {
			return readOnly(ctx);
		}
		return readOnly;
	}

	render() {
		const data = this.getData();
		const modified = data.get('detail') !== data.get('originalDetail');
		const ctx = new RenderContext(this.binder).withCalculatedProperties(data.get('calculateContextProperties'));
		const readOnly = this.isReadOnly(data, ctx);
		const centerToolbar = data.get('centerToolbar');
		ctx.setReadOnly(readOnly);
		const definition = data.get('definition');
		if (!definition) {
			return null;
		}
		const errorMessageList = data.getIn(errorMessageListPath);
		const modalModelState = data.get('modalModule');
		return (
			<ModuleContainer
				key={data.get('moduleKey') || 0}
				header={this.getHeader(data, ctx)}
				noModalCenterer={modalModelState && !errorMessageList}
				toolbarLeft={this.props.toolbarLeft}
				toolbarCenter={centerToolbar ? centerToolbar(ctx) : null}
				toolbarRight={!!data.get('saveUri') && (
					<React.Fragment>
						<Button
							type="light"
							text={getPhrase('cancelChanges')}
							disabled={!modified || readOnly}
							onAction={this.cancelChanges}
						/>
						<Button
							type="medium"
							text={getPhrase('save')}
							iconName="save"
							disabled={!modified || readOnly}
							onAction={this.save}
						/>
					</React.Fragment>
				)}
				modalDialog={errorMessageList ? (
					<ErrorMessageDialog
						data={errorMessageList}
						onBackToForm={this.clearErrorMessages}
						onSave={() => {
							this.clearErrorMessages();
							this.save(true);
						}}
					/>
				) : modalModelState ? (
					<ModalModule className="ps-entityDetailModalModule">
						<Module
							appState={modalModelState}
							onDataChange={newData => {
								this.setData(data.setIn(['modalModule', 'data'], newData));
							}}
							onCloseRequested={this.onModalCloseRequested}
						/>
					</ModalModule>
				) : null}
			>
				{definition.render(ctx)}
			</ModuleContainer>
		)
	}
}

const ModalModule = styled.div`
	top: 2%;
	left: 2%;
	position: absolute;
	width: 96%;
	height: 96%;
`;
/*
.mask {
  height: 157px;
  width: 410px;
  border-radius: 2px;
  background-color: #F8FAFB;
  box-shadow: 0 0 10px 0 rgba(0,0,0,0.2);
}
*/

const ErrorMessageDialogWrapper = styled.div`
	padding: ${props => props.theme.dialogPadding ?? defaultTheme.dialogPadding};
	border-radius: ${props => props.theme.dialogBorderRadius ?? defaultTheme.dialogBorderRadius};
	border: ${props => props.theme.dialogBorder ?? defaultTheme.dialogBorder};
	box-shadow: ${props => props.theme.dialogShadow ?? defaultTheme.dialogShadow};
	background-color: ${props => props.theme.dialogBackground ?? defaultTheme.dialogBackground};
`;

const DialogHeader = styled.div`
	font-weight: ${props => props.theme.dialogHeaderFontWeight ?? defaultTheme.dialogHeaderFontWeight};
	margin: 8px;
`

const Message = styled.div`
	margin-left: 24px;
`;

const MessageBlock = styled.div`
	margin-bottom: 24px;
`;

const Buttons = styled.div`
	text-align: right;
	[type=light] {
		margin-right:32px;
	}
`;

class ErrorMessageDialog extends React.PureComponent {
	renderMessages(messages) {
		return messages.map((msg, index) => {
			return (
				<Message key={index}>{msg}</Message>
			);
		});
	}

	renderMessageBlock(header, list) {
		if (list.size === 0) {
			return null;
		}
		return (
			<MessageBlock>
				<DialogHeader>{header}</DialogHeader>
				{this.renderMessages(list)}
			</MessageBlock>
		);
	}

	render() {
		const data = this.props.data;
		const warnings = data.get('warnings');
		const errors = data.get('errors');
		const unmodifiedErrors = data.get('unmodifiedErrors');
		return (
			<ErrorMessageDialogWrapper>
				{this.renderMessageBlock(getPhrase('validationMessageHeader'), errors)}
				{this.renderMessageBlock(errors.size > 0 ? getPhrase('validationIgnorableErrors2') : getPhrase('validationIgnorableErrors'), unmodifiedErrors)}
				{this.renderMessageBlock(getPhrase('validationWarnings'), warnings)}
				<Buttons>
					{
						errors.size === 0 && (
							<Button
								text={getPhrase('saveAnyway')}
								type="light"
								onAction={this.props.onSave}
							/>
						)
					}
					<Button
						text={getPhrase('backToForm')}
						onAction={this.props.onBackToForm}
					/>
				</Buttons>
			</ErrorMessageDialogWrapper>
		);
	}
}

